import { useState, useCallback, useMemo } from "react";
import type { Journal } from "@vlab-corporation/vlab-pilates-service-interface/class";
import type {
  ToggleField,
  GroupJournalFieldState,
  GroupMember,
  SharedJournalFields,
} from "./group-journal-utils";
import {
  ALL_TOGGLEABLE_FIELDS,
  TOGGLEABLE_FIELDS,
  initializeToggleFields,
  switchToShared,
  switchToPerMember,
  hasDataLossRisk,
  buildJournalDocuments,
  validateToggleFields,
} from "./group-journal-utils";
import type { JournalDocument } from "@/services/firestore/journals";

export interface UseGroupJournalFormParams {
  members: GroupMember[];
  existingJournals: Journal[];
  isOt: boolean;
}

export interface UseGroupJournalFormReturn {
  /** 토글 필드 상태 */
  toggleFields: Record<ToggleField, GroupJournalFieldState>;
  /** 필드 값 변경 (shared 모드) */
  setSharedValue: (field: ToggleField, value: string) => void;
  /** 필드 값 변경 (per-member 모드) */
  setPerMemberValue: (field: ToggleField, memberId: number, value: string) => void;
  /** 토글 전환 요청 → { needsConfirm, execute } */
  requestToggle: (field: ToggleField, targetMode: "shared" | "per-member") => ToggleRequest;
  /** 현재 활성화된 토글 필드 목록 (isOt에 따라 다름) */
  activeToggleFields: ToggleField[];
  /** 검증 오류 필드 목록 */
  validate: (requiredFields: ToggleField[]) => ToggleField[];
  /** 저장용 journal 문서 생성 */
  buildDocuments: (
    sharedFields: SharedJournalFields,
  ) => Array<Omit<JournalDocument, "createdAt" | "updatedAt">>;
  /** 회원 목록 */
  members: GroupMember[];
}

export interface ToggleRequest {
  /** 전환 시 데이터 유실 위험이 있는지 */
  needsConfirm: boolean;
  /** 전환 실행 */
  execute: () => void;
}

export function useGroupJournalForm({
  members,
  existingJournals,
  isOt,
}: UseGroupJournalFormParams): UseGroupJournalFormReturn {
  const [toggleFields, setToggleFields] = useState<Record<ToggleField, GroupJournalFieldState>>(
    () => initializeToggleFields(existingJournals, members),
  );

  const activeToggleFields = useMemo<ToggleField[]>(() => {
    return isOt ? ALL_TOGGLEABLE_FIELDS : TOGGLEABLE_FIELDS;
  }, [isOt]);

  const setSharedValue = useCallback((field: ToggleField, value: string) => {
    setToggleFields((prev) => ({
      ...prev,
      [field]: { ...prev[field], shared: value },
    }));
  }, []);

  const setPerMemberValue = useCallback((field: ToggleField, memberId: number, value: string) => {
    setToggleFields((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        perMember: { ...prev[field].perMember, [memberId]: value },
      },
    }));
  }, []);

  const requestToggle = useCallback(
    (field: ToggleField, targetMode: "shared" | "per-member"): ToggleRequest => {
      const currentState = toggleFields[field];

      if (currentState.mode === targetMode) {
        return { needsConfirm: false, execute: () => {} };
      }

      if (targetMode === "shared") {
        // per-member → shared: 데이터 유실 위험 체크
        const needsConfirm = hasDataLossRisk(currentState);
        return {
          needsConfirm,
          execute: () => {
            setToggleFields((prev) => ({
              ...prev,
              [field]: switchToShared(prev[field], members),
            }));
          },
        };
      } else {
        // shared → per-member: 항상 안전
        return {
          needsConfirm: false,
          execute: () => {
            setToggleFields((prev) => ({
              ...prev,
              [field]: switchToPerMember(prev[field], members),
            }));
          },
        };
      }
    },
    [toggleFields, members],
  );

  const validate = useCallback(
    (requiredFields: ToggleField[]) => {
      return validateToggleFields(toggleFields, requiredFields);
    },
    [toggleFields],
  );

  const buildDocuments = useCallback(
    (sharedFields: SharedJournalFields) => {
      return buildJournalDocuments({ members, toggleFields, sharedFields });
    },
    [members, toggleFields],
  );

  return {
    toggleFields,
    setSharedValue,
    setPerMemberValue,
    requestToggle,
    activeToggleFields,
    validate,
    buildDocuments,
    members,
  };
}
